import { useEffect } from 'react';

import { useEditorActions, useEditorStore, useTemporal } from '@/store/editor.store';

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}

/**
 * Global hotkeys for the editor. Most shortcuts are suppressed while focus is
 * inside an editable field (Tiptap, inputs) so they don't break native typing.
 */
export function useEditorHotkeys() {
  const actions = useEditorActions();
  const undo = useTemporal((s) => s.undo);
  const redo = useTemporal((s) => s.redo);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;

      // Undo / redo work even from inside text editor (matches Figma/Notion).
      if (meta && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redo(1);
        else undo(1);
        return;
      }
      if (meta && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        redo(1);
        return;
      }

      if (isEditableTarget(e.target)) return;

      if (meta && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        actions.duplicateSelection();
        return;
      }
      if (meta && (e.key === 'c' || e.key === 'C')) {
        actions.copySelection();
        return;
      }
      if (meta && (e.key === 'v' || e.key === 'V')) {
        actions.pasteSelection();
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        actions.deleteSelection();
        return;
      }

      if (e.key === 'Escape') {
        useEditorStore.getState().setSelection(null);
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        const state = useEditorStore.getState();
        const sel = state.selection;
        const script = state.script;
        if (!sel || !script) return;
        if (sel.kind === 'slide') {
          const i = script.slides.findIndex((s) => s.id === sel.slideId);
          const next = script.slides[i + dir];
          if (next) state.setSelection({ kind: 'slide', slideId: next.id });
        } else if (sel.kind === 'row' || sel.kind === 'column' || sel.kind === 'element') {
          const slide = script.slides.find((s) => s.id === sel.slideId);
          if (!slide) return;
          const i = slide.rows.findIndex((r) => r.id === sel.rowId);
          const nextRow = slide.rows[i + dir];
          if (nextRow) state.setSelection({ kind: 'row', slideId: slide.id, rowId: nextRow.id });
        }
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions, undo, redo]);
}
